import {
  BOARD_COLS,
  BOARD_ROWS,
  DIE_FACES,
  FINAL_CELL,
  START_CELL,
  type LaddersBoard,
  type LaddersJump,
  type LaddersLevel,
} from "./types";

/* ------------------------------------------------------------------ *
 * Board geometry, the move rule, and the layout validator — pure.
 * The engine (batch A) and the web's board (batch B1) both read these,
 * so a cell is in ONE place for the rules, the picture and the tests.
 * ------------------------------------------------------------------ */

/**
 * Where cell `n` (1..100) sits: `row` counted from the BOTTOM (0..9), `col` from the LEFT (0..9).
 *
 * BOUSTROPHEDON, the classic numbering: 1 is bottom-left, row 0 runs left→right to 10, row 1
 * runs right→left from 11 above 10 to 20, … and 100 ends top-LEFT — which is why the picture's
 * temple sits at the top-left. ⚠️ NEVER MIRRORED FOR RTL: the board is drawn in SVG from these
 * numbers, so `dir="rtl"` cannot flip it (a CSS grid would inherit it and silently move 100).
 */
export function cellCoord(n: number): { readonly row: number; readonly col: number } {
  if (!Number.isInteger(n) || n < 1 || n > FINAL_CELL) {
    throw new RangeError(`cellCoord: ${n} is not a cell (1..${FINAL_CELL})`);
  }
  const row = Math.floor((n - 1) / BOARD_COLS);
  const along = (n - 1) % BOARD_COLS;
  return { row, col: row % 2 === 0 ? along : BOARD_COLS - 1 - along };
}

/** The cell at (`row` from the bottom, `col` from the left) — the inverse of {@link cellCoord}. */
export function cellAt(row: number, col: number): number {
  if (row < 0 || row >= BOARD_ROWS || col < 0 || col >= BOARD_COLS) {
    throw new RangeError(`cellAt: (${row}, ${col}) is off the board`);
  }
  const along = row % 2 === 0 ? col : BOARD_COLS - 1 - col;
  return row * BOARD_COLS + along + 1;
}

/** Rows between two cells (≥ 0) — a snake's or ladder's visual length, and its animation's. */
export function rowSpan(a: number, b: number): number {
  return Math.abs(cellCoord(a).row - cellCoord(b).row);
}

/** The snake or ladder that STARTS on `cell`, if any (endpoints are distinct, so at most one). */
export function jumpAt(board: LaddersBoard, cell: number): LaddersJump | null {
  const s = board.snakes.findIndex((x) => x.head === cell);
  if (s >= 0) {
    const snake = board.snakes[s];
    if (snake) return { kind: "snake", index: s, from: cell, to: snake.tail };
  }
  const l = board.ladders.findIndex((x) => x.foot === cell);
  if (l >= 0) {
    const ladder = board.ladders[l];
    if (ladder) return { kind: "ladder", index: l, from: cell, to: ladder.top };
  }
  return null;
}

/** What one die does to a piece standing on `from` — the RULE, independent of whose turn it is. */
export interface RollOutcome {
  /** One cell per pip (from 3 a 4 is `[4, 5, 6, 7]`); EMPTY on an overshoot. */
  readonly path: readonly number[];
  /** The roll would have passed 100, so the piece did not move (`path` empty, `to === from`). */
  readonly overshoot: boolean;
  readonly landed: number;
  readonly jump: LaddersJump | null;
  readonly to: number;
}

/**
 * Move a piece from `from` (0..99) by `die` (1..6) — THE OPERATOR'S RULE (2026-11-03, which
 * replaced an earlier «a higher roll bounces back»):
 *  1. a roll that would go PAST 100 does not move the piece at all — it stays where it is, and
 *     nothing on its cell fires (a piece never stands on a snake's head or a ladder's foot);
 *  2. otherwise walk one cell per pip, and THEN take the snake or ladder that starts on the cell
 *     the walk ended on (one at most: no chains). A cell merely walked past does nothing.
 * Reaching 100 exactly wins; no snake or ladder ever touches 100.
 * (The operator's reason, from a player: «There are always at least two snakes up there between
 * 90 and 100. If it works like this, we'll just keep going down.»)
 */
export function resolveRoll(board: LaddersBoard, from: number, die: number): RollOutcome {
  if (!Number.isInteger(from) || from < START_CELL || from >= FINAL_CELL) {
    throw new RangeError(`resolveRoll: a piece on ${from} cannot move`);
  }
  if (!Number.isInteger(die) || die < 1 || die > DIE_FACES) {
    throw new RangeError(`resolveRoll: ${die} is not a die face`);
  }
  if (from + die > FINAL_CELL) {
    return { path: [], overshoot: true, landed: from, jump: null, to: from };
  }
  const path = Array.from({ length: die }, (_, pip) => from + pip + 1);
  const landed = from + die;
  const jump = jumpAt(board, landed);
  return { path, overshoot: false, landed, jump, to: jump ? jump.to : landed };
}

/* ---- validation ------------------------------------------------------------------------ */

/** Bounds a shipped board must meet — see {@link boardProblems}. */
export const BOARD_RULES = {
  minSnakes: 5,
  maxSnakes: 10,
  minLadders: 5,
  maxLadders: 10,
  /**
   * The expected number of rolls ONE player needs from 0 to 100 (bonus rolls counted as rolls).
   * Bounds the game's length: ~30 rolls × 4 players × ~2.7 s a roll is a ~5–6 minute game.
   */
  minExpectedRolls: 18,
  maxExpectedRolls: 40,
} as const;

/**
 * ROUND 2 — EACH LEVEL'S BAND of {@link expectedRolls} (the operator: «آسان, متوسط and سخت»), inside
 * {@link BOARD_RULES}' global bounds. ⚠️ A SINGLE-PIECE METRIC: one player alone, from 0 to 100,
 * bonus rolls counted — CAPTURE IS NOT IN IT. A capture sends a piece back to 0, so a real game
 * runs longer with every opponent (more pieces to hit and be hit by); the bands rank the MAPS, not a
 * table's length. `test/boards.test.ts` pins every shipped map inside its level's band.
 *   - easy: long ladders, few and short snakes;
 *   - medium: round 1's six boards (30.51–34.44);
 *   - hard: long snakes, few and short ladders.
 */
export const LEVEL_EXPECTED_ROLLS: Readonly<
  Record<LaddersLevel, { readonly min: number; readonly max: number }>
> = {
  easy: { min: 22, max: 28 },
  medium: { min: 30, max: 35 },
  hard: { min: 36, max: 40 },
};

export interface BoardProblem {
  readonly code: string;
  readonly detail: string;
}

/** Every cell reachable from `start`, with the full move rule (overshoot stays, then snake/ladder). */
export function reachableFrom(board: LaddersBoard, start: number): Set<number> {
  const seen = new Set<number>([start]);
  const queue = [start];
  while (queue.length > 0) {
    const p = queue.shift() as number;
    if (p === FINAL_CELL) continue;
    for (let d = 1; d <= DIE_FACES; d++) {
      const { to } = resolveRoll(board, p, d);
      if (!seen.has(to)) {
        seen.add(to);
        queue.push(to);
      }
    }
  }
  return seen;
}

/**
 * The expected number of rolls for ONE player from `START_CELL` to 100 (an absorbing Markov
 * chain, solved by Gauss–Seidel iteration). `Infinity` when 100 is unreachable.
 */
export function expectedRolls(board: LaddersBoard): number {
  // Only defined when the game always ends: 100 reachable from every reachable cell.
  const reach = reachableFrom(board, START_CELL);
  for (const p of reach) {
    if (p !== FINAL_CELL && !reachableFrom(board, p).has(FINAL_CELL)) {
      return Number.POSITIVE_INFINITY;
    }
  }
  const next: number[][] = [];
  for (let p = START_CELL; p < FINAL_CELL; p++) {
    next[p] = Array.from({ length: DIE_FACES }, (_, i) => resolveRoll(board, p, i + 1).to);
  }
  const e = new Array<number>(FINAL_CELL + 1).fill(0);
  for (let iter = 0; iter < 100_000; iter++) {
    let delta = 0;
    for (let p = FINAL_CELL - 1; p >= START_CELL; p--) {
      if (!reach.has(p)) continue;
      let sum = 0;
      for (const to of next[p] as number[]) sum += e[to] as number;
      const value = 1 + sum / DIE_FACES;
      delta = Math.max(delta, Math.abs(value - (e[p] as number)));
      e[p] = value;
    }
    if (delta < 1e-9) return e[START_CELL] as number;
  }
  return Number.POSITIVE_INFINITY;
}

/**
 * How near (in cells, centre to centre) an END of one snake or ladder may come to another's line
 * before the two are DRAWN touching. The web draws a ladder 0.4 of a cell wide (rails ±0.2) and a
 * snake's body at most ±0.17 (`apps/web/test/ladders-geometry.test.ts` pins its widths against
 * these), so:
 *  - a snake's TAIL is a tip, a point: it touches a body it comes within {@link TIP_CLEARANCE} of;
 *  - a ladder's foot or top (its rails) and a snake's head (±0.2) are bodies themselves: they touch
 *    another within {@link END_CLEARANCE} — two half-widths.
 * A snake is judged on its chord (head → tail): its wiggle is the drawing's own.
 */
export const TIP_CLEARANCE = 0.25;
export const END_CLEARANCE = 0.4;

/** A cell's centre in cell units: x = column from the left, y = row from the bottom. */
function centre(n: number): readonly [number, number] {
  const { row, col } = cellCoord(n);
  return [col, row];
}

/** Where cell `p` sits against the line a→b: `t` along it (0 at a, 1 at b), `off` its distance. */
function against(a: number, b: number, p: number): { readonly t: number; readonly off: number } {
  const [ax, ay] = centre(a);
  const [bx, by] = centre(b);
  const [px, py] = centre(p);
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  return {
    t: ((px - ax) * dx + (py - ay) * dy) / len2,
    off: Math.abs((px - ax) * dy - (py - ay) * dx) / Math.sqrt(len2),
  };
}

/**
 * a→b and c→d lie on ONE line and overlap — or meet end to end with no more than one cell between
 * them: either way they are drawn as one long ladder (or snake).
 */
function onOneLine(a: number, b: number, c: number, d: number): boolean {
  const C = against(a, b, c);
  const D = against(a, b, d);
  if (C.off > 1e-9 || D.off > 1e-9) return false;
  const [ax, ay] = centre(a);
  const [bx, by] = centre(b);
  const len = Math.hypot(bx - ax, by - ay);
  const lo = Math.min(C.t, D.t) * len;
  const hi = Math.max(C.t, D.t) * len;
  return Math.max(lo - len, -hi) <= 1 + 1e-9;
}

/** Segments (cell centres, in cell units) properly cross — touching at an end does not count. */
function segmentsCross(a: number, b: number, c: number, d: number): boolean {
  const P = centre;
  const [p1, p2, p3, p4] = [P(a), P(b), P(c), P(d)];
  const orient = (
    p: readonly [number, number],
    q: readonly [number, number],
    r: readonly [number, number],
  ): number => Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
  const o1 = orient(p1, p2, p3);
  const o2 = orient(p1, p2, p4);
  const o3 = orient(p3, p4, p1);
  const o4 = orient(p3, p4, p2);
  return o1 * o2 < 0 && o3 * o4 < 0;
}

/**
 * Everything wrong with `board` — empty means it may ship. The board-validation test runs this
 * over every shipped layout; a new layout is added only once this returns `[]` for it. With a
 * `level`, the length must also sit inside that level's band ({@link LEVEL_EXPECTED_ROLLS}).
 */
export function boardProblems(board: LaddersBoard, level?: LaddersLevel): BoardProblem[] {
  const out: BoardProblem[] = [];
  const add = (code: string, detail: string): void => {
    out.push({ code, detail });
  };
  if (!/^[a-z0-9-]+$/.test(board.id)) add("id", `id «${board.id}» must be [a-z0-9-]+`);

  const { minSnakes, maxSnakes, minLadders, maxLadders } = BOARD_RULES;
  if (board.snakes.length < minSnakes || board.snakes.length > maxSnakes) {
    add("count", `${board.snakes.length} snakes (allowed ${minSnakes}..${maxSnakes})`);
  }
  if (board.ladders.length < minLadders || board.ladders.length > maxLadders) {
    add("count", `${board.ladders.length} ladders (allowed ${minLadders}..${maxLadders})`);
  }

  const endpoints: number[] = [];
  const cellOk = (n: number, what: string): boolean => {
    if (!Number.isInteger(n) || n <= 1 || n >= FINAL_CELL) {
      add("edge", `${what} on ${n}: nothing may touch 1 or 100, or leave the board`);
      return false;
    }
    return true;
  };
  board.snakes.forEach((s, i) => {
    const ok = cellOk(s.head, `snake ${i} head`) && cellOk(s.tail, `snake ${i} tail`);
    endpoints.push(s.head, s.tail);
    if (!ok) return;
    if (s.head <= s.tail) add("direction", `snake ${i} ${s.head}→${s.tail} must go DOWN`);
    else if (rowSpan(s.head, s.tail) < 1)
      add("flat", `snake ${i} ${s.head}→${s.tail} stays in one row`);
  });
  board.ladders.forEach((l, i) => {
    const ok = cellOk(l.foot, `ladder ${i} foot`) && cellOk(l.top, `ladder ${i} top`);
    endpoints.push(l.foot, l.top);
    if (!ok) return;
    if (l.top <= l.foot) add("direction", `ladder ${i} ${l.foot}→${l.top} must go UP`);
    else if (rowSpan(l.foot, l.top) < 1)
      add("flat", `ladder ${i} ${l.foot}→${l.top} stays in one row`);
  });

  // Every endpoint a different cell: no two starts on one cell, no end that is also a start
  // (that would be a CHAIN), and no two ends sharing a cell (two pieces of art on one cell).
  const counts = new Map<number, number>();
  for (const n of endpoints) counts.set(n, (counts.get(n) ?? 0) + 1);
  for (const [n, c] of counts) if (c > 1) add("shared", `cell ${n} is used by ${c} endpoints`);
  if (out.length > 0) return out; // the rest assumes a well-formed board

  // Drawability: two ladders that cross read as one broken ladder.
  for (let i = 0; i < board.ladders.length; i++) {
    for (let j = i + 1; j < board.ladders.length; j++) {
      const a = board.ladders[i] as (typeof board.ladders)[number];
      const b = board.ladders[j] as (typeof board.ladders)[number];
      if (segmentsCross(a.foot, a.top, b.foot, b.top)) add("cross", `ladders ${i} and ${j} cross`);
    }
  }

  // Drawability: nothing is drawn ON another. Two on one line read as one (b5's first draft:
  // ladders 20→41, 40→61 and 60→81 up the left edge, one long ladder from 20 to 81); an end on
  // another's body reads as a fork or a hidden end (b2's first draft: ladder 3→22 ended on 22,
  // which ladder 19→39 runs through). A proper crossing (an X, no end near) stays allowed for a
  // snake — the classic look; two ladders never cross (above).
  const items = [
    ...board.snakes.map((s, i) => ({
      name: `snake ${i} ${s.head}→${s.tail}`,
      a: s.head,
      b: s.tail,
      clear: [END_CLEARANCE, TIP_CLEARANCE] as const, // the head is drawn wide, the tail a tip
    })),
    ...board.ladders.map((l, i) => ({
      name: `ladder ${i} ${l.foot}→${l.top}`,
      a: l.foot,
      b: l.top,
      clear: [END_CLEARANCE, END_CLEARANCE] as const, // rails at both ends
    })),
  ];
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const A = items[i] as (typeof items)[number];
      const B = items[j] as (typeof items)[number];
      if (onOneLine(A.a, A.b, B.a, B.b)) {
        add("collinear", `${A.name} and ${B.name} lie on one line`);
        continue;
      }
      for (const [X, Y] of [
        [A, B],
        [B, A],
      ] as const) {
        for (const [end, clear] of [
          [Y.a, Y.clear[0]],
          [Y.b, Y.clear[1]],
        ] as const) {
          const { t, off } = against(X.a, X.b, end);
          if (t > 0 && t < 1 && off < clear) {
            add("touch", `${Y.name} ends on ${end}, on ${X.name}'s body`);
          }
        }
      }
    }
  }

  // Winnable from EVERY reachable cell, so no game can get stuck.
  const reach = reachableFrom(board, START_CELL);
  if (!reach.has(FINAL_CELL)) add("unwinnable", "100 is not reachable from the start");
  for (const p of reach) {
    if (p !== FINAL_CELL && !reachableFrom(board, p).has(FINAL_CELL)) {
      add("trap", `from ${p} 100 can never be reached`);
    }
  }

  const rolls = expectedRolls(board);
  const { minExpectedRolls, maxExpectedRolls } = BOARD_RULES;
  if (!(rolls >= minExpectedRolls && rolls <= maxExpectedRolls)) {
    add(
      "length",
      `expected ${rolls.toFixed(1)} rolls (allowed ${minExpectedRolls}..${maxExpectedRolls})`,
    );
  } else if (level !== undefined) {
    const band = LEVEL_EXPECTED_ROLLS[level];
    if (!(rolls >= band.min && rolls <= band.max)) {
      add("level", `expected ${rolls.toFixed(2)} rolls (${level}: ${band.min}..${band.max})`);
    }
  }
  return out;
}
